/**
 * @fileoverview EditTransactionModal component for editing existing transactions.
 * Similar to TransactionModal but uses edit state instead of add state.
 *
 * Includes conflict resolution for when another user modifies the same transaction.
 */

import { useState, useEffect } from 'react';
import EditTransactionForm from './EditTransactionForm';
import TransactionModalErrors from '../TransactionModal/TransactionModalErrors';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { ConflictModal } from '../Realtime/ConflictModal';
import { useConflictResolution } from '../../hooks/useConflictResolution';
import { ConflictError } from '../../services/api';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './EditTransactionModal.module.css';

/**
 * EditTransactionModal Component
 *
 * Modal overlay for editing existing transactions.
 * Displays the edit transaction form with save/cancel buttons.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - onClose (function): Called when modal should close
 * - transactionHook (object): Transaction management hook with edit state
 * - budgetStartMonth (string): Budget start month for date validation
 * - singleBankMode (boolean): Whether budget is in single bank mode
 */
function EditTransactionModal({ isOpen, onClose, transactionHook, budgetStartMonth, singleBankMode }) {
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Conflict resolution for concurrent edits
    const {
        conflict,
        isResolving,
        handleConflictError,
        resolveWithMyChanges,
        resolveWithServerChanges,
        dismissConflict
    } = useConflictResolution();

    // Reset saving state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const success = await transactionHook.saveEdit();
            if (success) {
                onClose();
                // Note: Don't set isSaving to false - modal will unmount
            } else {
                // Validation failed or API error - stay on modal
                setIsSaving(false);
            }
        } catch (error) {
            // Check if this is a conflict error
            if (error instanceof ConflictError || error.isConflict) {
                // Build the current form data for comparison
                // Use absolute amount since that's what user sees in the form
                const yourData = {
                    amount: parseFloat(transactionHook.editAmount),
                    description: transactionHook.editDescription,
                    date: transactionHook.editDate,
                    type: transactionHook.editTransactionType,
                    // Include categories with amounts for comparison
                    categories: transactionHook.editTransactionType === 'Adjustment'
                        ? `${transactionHook.editAdjustmentFromCategory} → ${transactionHook.editAdjustmentToCategory}: $${transactionHook.editAmount}`
                        : transactionHook.editTransactionCategories
                            .filter(c => c.category)
                            .map(c => `${c.category}: $${parseFloat(c.amount || 0).toFixed(2)}`)
                            .join(', ')
                };

                // Normalize server data to match form display (absolute amounts)
                if (error.currentData) {
                    error.currentData.amount = Math.abs(error.currentData.amount);
                }

                // Handle conflict - this will show the ConflictModal
                handleConflictError(error, yourData);
                setIsSaving(false);
            } else {
                // Other unexpected error
                setIsSaving(false);
            }
        }
    };

    /**
     * Handle "Keep My Changes" - retry save with current server version
     */
    const handleKeepMine = async () => {
        try {
            await resolveWithMyChanges(async (dataWithNewVersion) => {
                // Update the edit version to the current server version before retrying
                // This ensures the next save attempt uses the correct version
                transactionHook.setEditVersion(dataWithNewVersion.version);

                // Retry the save - it will now use the updated version
                const success = await transactionHook.saveEdit();
                if (success) {
                    onClose();
                }
                return success;
            });
        } catch (error) {
            // If still conflict, the hook will update the conflict state
            // If other error, show it
            if (!(error instanceof ConflictError) && !error.isConflict) {
                transactionHook.setEditErrors({ general: error.message || 'Failed to save changes.' });
            }
        }
    };

    /**
     * Handle "Accept Their Changes" - close the edit modal and discard user's changes.
     * The realtime system will have already updated the transaction list with the new data,
     * so the user can re-open the transaction to see/edit the current version.
     */
    const handleAcceptTheirs = () => {
        resolveWithServerChanges(); // Clear the conflict state
        transactionHook.cancelEdit(); // Clear the edit form
        onClose(); // Close the modal
    };

    /**
     * Handle conflict cancel - dismiss conflict modal and stay on edit form
     */
    const handleConflictCancel = () => {
        dismissConflict();
    };

    const handleCancel = () => {
        transactionHook.cancelEdit();
        transactionHook.setEditErrors({}); // Clear validation errors (including general error)
        onClose();
    };

    return (
        <>
            {/* Dark Overlay */}
            <div className={styles.overlay}>
                {/* Modal Content */}
                <div className={styles.modal}>
                    {/* Close X Button */}
                    <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className={styles.closeButton}
                        aria-label="Close"
                    >
                        ×
                    </button>

                    {/* Modal Header */}
                    <div className={styles.header}>
                        <h2 className={styles.title}>Edit Transaction</h2>
                    </div>

                    {/* Modal Body */}
                    <div className={styles.body}>
                        <EditTransactionForm
                            transactionHook={transactionHook}
                            budgetStartMonth={budgetStartMonth}
                            singleBankMode={singleBankMode}
                        />
                    </div>

                    {/* Modal Footer */}
                    <div className={styles.footer}>
                        {/* Error Messages */}
                        <TransactionModalErrors errors={transactionHook.editErrors} />

                        {/* Buttons */}
                        <div className={styles.buttonGroup}>
                            <button
                                onClick={handleCancel}
                                disabled={isSaving || isResolving}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSave}
                                disabled={isSaving || isResolving}
                                className={styles.submitButton}
                            >
                                {isSaving && <ButtonSpinner />}
                                {isSaving ? 'Saving Changes...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Conflict Resolution Modal */}
            <ConflictModal
                conflict={conflict}
                onKeepMine={handleKeepMine}
                onAcceptTheirs={handleAcceptTheirs}
                onCancel={handleConflictCancel}
                isResolving={isResolving}
                resourceType="transaction"
            />
        </>
    );
}

export default EditTransactionModal;
